// src/components/Notes/NoteEditor.tsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Maximize2, Minimize2, Trash2, Upload, Eye, EyeOff, ChevronDown, ChevronUp, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const TODO_STATUSES = ['待處理', '進行中', '已完成', '暫擱置'] as const;
type TodoStatus = typeof TODO_STATUSES[number];
type NoteType = 'memo' | 'password' | 'todo';

// 壓縮圖片函數
const compressImage = (file: File, maxWidth: number = 800, maxHeight: number = 800, quality: number = 0.7): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new window.Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = height * (maxWidth / width);
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = width * (maxHeight / height);
          height = maxHeight;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        const compressed = canvas.toDataURL('image/jpeg', quality);
        resolve(compressed);
      };
      img.onerror = () => reject(new Error('圖片載入失敗'));
    };
    reader.onerror = () => reject(new Error('檔案讀取失敗'));
  });
};

// 手機偵測
const isMobileDevice = (): boolean => {
  if (window.innerWidth < 768) return true;
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua.toLowerCase());
};

interface NoteEditorProps {
  isOpen: boolean;
  note?: any;
  defaultType?: NoteType;
  onSave: (id: string, title: string, content: string, category: string, tags: string[], images: string[], type: NoteType, username?: string, password?: string, due_date?: string, status?: string) => Promise<void>;
  onClose: () => void;
}

export const NoteEditor: React.FC<NoteEditorProps> = ({ isOpen, note, defaultType = 'memo', onSave, onClose }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('一般');
  const [tags, setTags] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [type, setType] = useState<NoteType>('memo');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [status, setStatus] = useState<TodoStatus>('待處理');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = isMobileDevice();

  // 1. 開啟/關閉時載入與重置狀態
  useEffect(() => {
    // 關閉時重置所有欄位
    if (!isOpen) {
      setTitle('');
      setContent('');
      setCategory('一般');
      setTags('');
      setImages([]);
      setType(defaultType || 'memo');
      setUsername('');
      setPassword('');
      setDueDate('');
      setStatus('待處理');
      setShowMoreFields(false);
      return;
    }

    // 手機版預設自動全螢幕
    if (isMobile) {
      setIsFullscreen(true);
    }

    // 載入編輯資料或初始化
    if (note) {
      setTitle(note.title || '');
      setContent(note.content || '');
      setCategory(note.category || '一般');
      setTags(note.tags?.join(', ') || '');
      setImages(Array.isArray(note.images) ? note.images : []);
      setType(note.type || 'memo');
      setUsername(note.username || '');
      setPassword(note.password || '');
      setDueDate(note.due_date || '');
      setStatus(note.status || '待處理');
    } else {
      setTitle('');
      setContent('');
      setCategory('一般');
      setTags('');
      setImages([]);
      setType(defaultType || 'memo');
      setUsername('');
      setPassword('');
      setDueDate('');
      setStatus('待處理');
    }
  }, [isOpen, note, defaultType, isMobile]);

  // 2. 華為瀏覽器鍵盤相容處理
  useEffect(() => {
    if (!isOpen) return;
    const handleFocus = () => {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    };
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('focus', handleFocus);
    }
    return () => {
      if (textarea) {
        textarea.removeEventListener('focus', handleFocus);
      }
    };
  }, [isOpen]);

  // 3. 監聽內容變更
  useEffect(() => {
    const hasUnsavedChanges = 
      title !== (note?.title || '') ||
      content !== (note?.content || '') ||
      tags !== (note?.tags?.join(', ') || '') ||
      images.length !== (note?.images?.length || 0);
    
    setHasChanges(hasUnsavedChanges);
  }, [title, content, tags, images, note]);

  // ESC 鍵監聽
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
      if (e.key === 'Escape' && lightboxImage) {
        setLightboxImage(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isFullscreen, lightboxImage]);

  const handleClose = () => {
    if (hasChanges) {
      if (confirm('有未儲存的變更，確定要關閉嗎？')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 貼圖處理
  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          if (file.size > 20 * 1024 * 1024) {
            toast.error('圖片太大，請壓縮後再貼上 (限制 20MB)');
            return;
          }
          try {
            toast.loading('壓縮圖片中...', { id: 'compress' });
            const compressed = await compressImage(file, 800, 800, 0.7);
            toast.dismiss('compress');
            setImages(prev => [...prev, compressed]);
            toast.success('圖片已插入！');
          } catch (err) {
            toast.error('圖片處理失敗');
          }
        }
      }
    }
  };

  // 檔案上傳
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} 不是圖片格式`);
        continue;
      }
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`圖片 ${file.name} 太大，請壓縮後再上傳 (限制 20MB)`);
        continue;
      }
      try {
        toast.loading(`壓縮 ${file.name}...`, { id: 'compress' });
        const compressed = await compressImage(file, 800, 800, 0.7);
        toast.dismiss('compress');
        setImages(prev => [...prev, compressed]);
        toast.success(`圖片 ${file.name} 已插入！`);
      } catch (err) {
        toast.error(`圖片 ${file.name} 處理失敗`);
      }
    }
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    toast.success('圖片已移除');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('請輸入標題');
      return;
    }
    if (type === 'password') {
      if (!username.trim()) {
        toast.error('請輸入使用者名稱');
        return;
      }
      if (!password.trim()) {
        toast.error('請輸入密碼');
        return;
      }
    }
    if (type === 'todo') {
      if (!dueDate) {
        toast.error('請選擇完成日期');
        return;
      }
    }
    setIsLoading(true);
    try {
      const tagList = tags.split(',').map(t => t.trim()).filter(t => t);
      await onSave(
        note?.id || 'new',
        title.trim(),
        content.trim(),
        category,
        tagList,
        images,
        type,
        username.trim(),
        password.trim(),
        dueDate,
        status
      );
      toast.success(note ? '筆記已更新' : '筆記已建立');
      onClose();
    } catch (error) {
      toast.error('儲存失敗');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const isPasswordType = type === 'password';
  const isTodoType = type === 'todo';

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/60 z-50 transition-all flex items-center justify-center ${
          isFullscreen ? 'p-0' : 'p-4 sm:p-6'
        }`}
        onClick={handleClose}
      >
        <div
          className={`bg-white dark:bg-gray-800 flex flex-col transition-all duration-200 overflow-hidden ${
            isFullscreen
              ? 'w-full h-full rounded-none shadow-none'
              : 'w-[95vw] max-w-[1000px] h-[85vh] max-h-[900px] rounded-2xl shadow-2xl'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50/50 dark:bg-gray-800/50">
            <button
              type="button"
              onClick={handleClose}
              className="px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
            >
              取消
            </button>

            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {note ? '編輯筆記' : '新增筆記'}
            </h2>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={toggleFullscreen}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-gray-500 transition-colors"
                title={isFullscreen ? '切換為彈窗模式' : '切換為全螢幕'}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                {isLoading ? '儲存中...' : <><Check className="w-4 h-4" /> 儲存</>}
              </button>
            </div>
          </div>

          {/* Form 本體 */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto flex flex-col p-4 sm:p-6 space-y-4">
            
            {/* 類型切換 */}
            <div className="flex gap-2">
              {(['memo', 'password', 'todo'] as NoteType[]).map((t) => (
								<button
									key={t}
									type="button"
									onClick={() => setType(t)}
									className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
										type === t
											? 'bg-blue-600 text-white'
											: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
									}`}
								>
									{t === 'memo' && '📝 記事'}
									{t === 'password' && '🔒 密碼'}
									{t === 'todo' && '✅ 待辦'}
								</button>
              ))}
            </div>

            {/* 標題輸入 */}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-xl font-bold bg-transparent border-b border-gray-200 dark:border-gray-700 pb-2 focus:border-blue-500 focus:outline-none dark:text-white placeholder-gray-400"
              placeholder="輸入標題..."
              required
            />

            {/* 密碼 / 待辦 專屬欄位 */}
            {isPasswordType && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50 dark:bg-gray-700/40 p-3 rounded-xl">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                  placeholder="使用者名稱"
                />
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white pr-10"
                    placeholder="密碼"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 p-1"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            {isTodoType && (
              <div className="grid grid-cols-2 gap-3 bg-gray-50 dark:bg-gray-700/40 p-3 rounded-xl">
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                />
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as TodoStatus)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                >
                  {TODO_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}

            {/* 內文 Textarea */}
            <div className="flex-1 min-h-[200px] flex flex-col">
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onPaste={handlePaste}
                className="w-full flex-1 min-h-[180px] p-2 bg-transparent focus:outline-none dark:text-white resize-none font-mono text-base leading-relaxed rounded-lg border border-gray-300 dark:border-gray-600"
                placeholder="開始寫點什麼吧..."
              />
            </div>

            {/* 可折疊欄位 (分類 & 標籤) */}
            <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
              <button
                type="button"
                onClick={() => setShowMoreFields(!showMoreFields)}
                className="flex items-center justify-between w-full text-xs text-gray-500 py-1"
              >
                <span>更多設定 (分類：{category} / 標籤)</span>
                {showMoreFields ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {showMoreFields && (
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                  >
                    <option value="一般">一般</option>
                    <option value="工作">工作</option>
                    <option value="個人">個人</option>
                    <option value="學習">學習</option>
                  </select>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                    placeholder="標籤1, 標籤2"
                  />
                </div>
              )}
            </div>

            {/* 圖片預覽 */}
            {images.length > 0 && (
              <div className="flex gap-2 overflow-x-auto py-2">
                {images.map((img, index) => (
                  <div key={index} className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                    <img src={img} alt="縮圖" className="w-full h-full object-cover cursor-pointer" onClick={() => setLightboxImage(img)} />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </form>

          {/* 底部工具列 */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between flex-shrink-0">
            <label className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl cursor-pointer text-sm font-medium shadow-sm active:scale-95 transition-transform">
              <Upload className="w-4 h-4 text-blue-500" />
              <span>插入圖片 ({images.length})</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={handleFileUpload} />
            </label>

            <span className="text-xs text-gray-400">
              {content.length} 字
            </span>
          </div>

        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[999] p-4"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img src={lightboxImage} alt="預覽" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" />
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/80 rounded-full p-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};